import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { Layout } from './components/layout/siteLayout';
import NextRoot from './next/NextRoot';
import { checkToken, cleanupToken } from './common/auth';
import { getUser } from './api/_profiles';
import { fetchProductCategories, fetchProductSchemes } from './api/_categories';
import { getSystemTypes } from './api/_integrations';
import { getDetailedError } from './common/utils';
import ErrorBoundary from './components/misc/ErrorBoundary';
import * as logger from './logging/logger';
import timeZoneName from './common/timezone';
import addIcons from './common/icons';
import { readLanguageFromUrl, isSupported, getDefaultLanguageCode } from './languages';

import 'normalize.css';
import 'react-day-picker/dist/style.css';
import '../styles/master.scss';
// Scoped entirely to `.next-root`; see the header of that file.
import './next/styles/master.scss';
import 'react-datepicker/dist/react-datepicker.css';

import {
  setLanguage,
  setTimeZone,
  storeUser,
  storeCategories,
  storeCurrencies,
  storeSchemes,
  storeSystemTypes
}
  from './store';

addIcons();

const storeModule = require('./store');

const createStore = storeModule.default;

function showMainLoader() {
  const app = document.getElementById('app');
  const loader = document.getElementById('initial-loader');

  loader.style.display = 'block';
  app.style.display = 'none';
}

function hideMainLoader() {
  const app = document.getElementById('app');
  const loader = document.getElementById('initial-loader');

  loader.style.display = 'none';
  app.style.display = 'block';
}

function initializeGlobalError() {
  window.onerror = async (error, file, line, command, fullError) => {
    const errorDetails = (await getDetailedError(fullError));

    logger.error(`Error occurred: ${error} ${errorDetails}`);
  };
}

function detectLanguage() {
  const langFromUrl = readLanguageFromUrl(window.location.pathname);
  if (langFromUrl && isSupported(langFromUrl)) {
    return langFromUrl;
  }

  const browserLangs = navigator.languages || [navigator.language || 'en'];
  for (const lang of browserLangs) {
    const code = lang.split('-')[0];
    if (isSupported(code)) {
      return code;
    }
  }

  return getDefaultLanguageCode();
}

async function prepareDataAndAuth() {
  const language = detectLanguage();
  const timeZoneFromClient = timeZoneName();

  let store = createStore({ language, timeZone: timeZoneFromClient });

  if (checkToken()) {
    showMainLoader();
    const userResult = await getUser(language, timeZoneFromClient);

    let { success } = userResult;
    let categories = null;
    let schemes = null;
    let systemTypes = null;

    if (success) {
      try {
        const categoriesTask = fetchProductCategories(language, timeZoneFromClient);
        const schemesTask = fetchProductSchemes(language, timeZoneFromClient);
        const systemTypesTask = getSystemTypes(language, timeZoneFromClient);
        const { data: user } = userResult;
        categories = await categoriesTask;
        schemes = await schemesTask;
        systemTypes = await systemTypesTask;
        store = createStore();
        store.dispatch(storeUser(user));
        store.dispatch(storeCategories(categories));
        store.dispatch(storeSchemes(schemes));
        store.dispatch(storeCurrencies([{ Name: 'DKK' }]));
        store.dispatch(storeSystemTypes(systemTypes));
      }
      catch {
        success = false;
      }
    }

    if (!success) {
      cleanupToken();
    }
    hideMainLoader();
  }

  store.dispatch(setLanguage(language));
  store.dispatch(setTimeZone(timeZoneFromClient));

  return { store, language };
}

function initializeLog() {
  logger.addTransport('console', logger.severity.debug, (message, level) => {
    if (level <= logger.severity.error) {
      console.error(message);
    }
    else {
      console.log(message);
    }
  });
}

function renderErrorPage() {
  document.body.innerHTML = '<h1>Page not available<h1><h3>Try again later</h3>';
}

function renderPage(store) {
  const jsx = (
    <ReduxProvider store={store}>
      <ErrorBoundary>
        <Router>
          {/*
            The rebuilt admin lives at /next/* with its own shell and its own
            stylesheet. Mounting it as a sibling of Layout rather than inside it
            is what keeps the two apart: every other path still reaches Layout
            exactly as before, and nothing under /next can style a page outside
            it.
          */}
          <Routes>
            <Route path="/next/*" element={<NextRoot />} />
            <Route path="*" element={<Layout />} />
          </Routes>
        </Router>
      </ErrorBoundary>
    </ReduxProvider>
  );

  const app = document.getElementById('app');
  const root = createRoot(app);
  root.render(jsx);
}

async function start() {
  initializeLog();

  initializeGlobalError();

  try {
    const { store } = await prepareDataAndAuth();

    renderPage(store);
  }
  catch (e) {
    renderErrorPage();
    logger.error(`Unhandled error: ${await getDetailedError(e)}`);
  }
}

start();
